// src/components/groups/GroupsGrid.jsx
// Groups grid component - displays joined and owned groups with radio toggle
// Redesigned January 2026: Two-mode interface with horizontal cards
// Layout: Fixed radio bar at top, scrollable cards, fixed suggested groups at bottom

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';
import { groupService } from '../../data/dataService';
import useGroups from '../../hooks/useGroups';
import JoinedGroupCard from './JoinedGroupCard';
import OwnedGroupCard from './OwnedGroupCard';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { getChildGroups, getSubgroupCount } from '../../utils/groupHelpers';
import { rateLimitMessage } from '../../utils/rateLimitMessage';

const GroupsGrid = ({ onGroupSelect, currentUser, sessionReady = false, initialMode = 'joined', selectedArea = null }) => {
  const theme = useTheme();
  const { requireAuth } = useAuth();
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation('groups');

  // View mode state - 'joined', 'owned', or 'discover'
  const [viewMode, setViewMode] = useState(initialMode);

  // Groups data + click-through logic — shared with GroupsColumn via useGroups (July 2026)
  const {
    joinedGroups,
    ownedGroups,
    suggestedGroups,
    discoverGroups,
    loading,
    selectGroup,
    visitGroup,
    joinGroup
  } = useGroups({ currentUser, sessionReady, onGroupSelect, selectedArea });

  // State for hierarchical display (owned groups)
  const [childGroupsCache, setChildGroupsCache] = useState({});

  // State for search
  const [searchTerm, setSearchTerm] = useState('');

  // Minimal feedback for a blocked join attempt — this grid has no other join-error display
  // today (pre-existing; other join surfaces' toasts aren't duplicated here for that).
  const [joinFeedback, setJoinFeedback] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // State for suggested groups scrolling
  const suggestedScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Show the loading indicator while the session is still restoring (!sessionReady)
  // OR a fetch is in flight (loading). The "no groups" empty states render ONLY when
  // sessionReady && !loading && genuinely zero — so "session not resolved yet" can
  // never be mistaken for "you have no groups".
  const showLoading = !sessionReady || loading;

  // Update view mode when initialMode prop changes
  useEffect(() => {
    setViewMode(initialMode);
  }, [initialMode]);

  // Listen for group search events from HeaderButtonsFeed
  useEffect(() => {
    const handleSearchEvent = (event) => {
      const { searchTerm } = event.detail;
      handleGroupSearch(searchTerm);
    };

    window.addEventListener('ournet:search-groups', handleSearchEvent);
    return () => window.removeEventListener('ournet:search-groups', handleSearchEvent);
  }, []);

  // Handle group search (server-side ILIKE via search_communities RPC)
  const handleGroupSearch = async (term) => {
    setSearchTerm(term);
    if (term.trim()) {
      setIsSearching(true);
      console.log('🔍 Searching groups:', term);
      try {
        const results = await groupService.searchCommunities(term);
        setSearchResults(results || []);
        console.log('✅ Search results:', results?.length);
      } catch (error) {
        console.error('❌ Error searching groups:', error);
        setSearchResults([]);
      }
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  // Load children for owned groups (for hierarchy display)
  useEffect(() => {
    const loadHierarchyData = async () => {
      const allGroups = ownedGroups;
      if (allGroups.length === 0) return;

      const children = {};

      // Build a lookup by ID (has deleted_at, deletion_scheduled_at)
      const groupById = {};
      allGroups.forEach(g => { groupById[g.id] = g; });

      // Enrich child group data with fields from parent list
      const enrichChild = (child) => {
        const parent = groupById[child.id];
        if (parent) {
          return { ...child, deleted_at: parent.deleted_at, deletion_scheduled_at: parent.deletion_scheduled_at, owner_id: parent.owner_id };
        }
        return child;
      };

      for (const group of allGroups) {
        if (children[group.id]) continue; // Already processed
        const count = await getSubgroupCount(group.id);
        if (count > 0) {
          children[group.id] = (await getChildGroups(group.id)).map(enrichChild);
          for (const child of children[group.id]) {
            if (children[child.id]) continue;
            const childCount = await getSubgroupCount(child.id);
            if (childCount > 0) {
              children[child.id] = (await getChildGroups(child.id)).map(enrichChild);
            }
          }
        }
      }

      setChildGroupsCache(children);
    };

    loadHierarchyData();
  }, [ownedGroups]);

  // Direct select — for Joined groups (already a member, no landing check needed)
  const handleGroupClick = selectGroup;

  // Landing-page-first visit — for Owned/Discover/Suggested/Search
  const handleVisitSuggestedGroup = visitGroup;

  const handleCreateGroup = () => {
    console.log('Create Group button clicked');
    // Check auth before navigating to create form
    requireAuth('gate_body_create_group', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'new-group' }
      }));
    });
  };


  const handleJoinGroup = (group) => joinGroup(group, requireAuth, (result) => {
    if (result?.status === 'blocked') {
      setJoinFeedback(t('cannot_request_to_join'));
      setTimeout(() => setJoinFeedback(''), 3000);
    } else if (result?.status === 'rate_limited') {
      setJoinFeedback(rateLimitMessage(result.error, t) || t('common:rate_limit_generic'));
      setTimeout(() => setJoinFeedback(''), 6000);
    }
  });

  // Check scroll position for arrow visibility
  const checkScrollPosition = () => {
    const container = suggestedScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  // Update arrow visibility when suggested groups change
  useEffect(() => {
    checkScrollPosition();
    const container = suggestedScrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      return () => container.removeEventListener('scroll', checkScrollPosition);
    }
  }, [suggestedGroups]);

  // Scroll suggested groups
  const scrollSuggested = (direction) => {
    const container = suggestedScrollRef.current;
    if (!container) return;

    const scrollAmount = 260; // Slightly more than card width (240px + gap)
    const newScrollLeft = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  // Filter to get only top-level groups (no parent)
  const topLevelOwnedGroups = ownedGroups.filter(group => !group.parent_group_id);
  const topLevelJoinedGroups = joinedGroups.filter(group => !group.parent_group_id);

  // Render hierarchical groups with connector lines
  const renderGroupHierarchy = (group, level = 1) => {
    const children = childGroupsCache[group.id] || [];
    const hasChildren = children.length > 0;

    return (
      <div
        key={group.id}
        className={`hierarchy-group ${level > 1 ? 'hierarchy-child' : ''} ${level === 3 ? 'level-3' : ''}`}
        style={{
          position: 'relative',
          marginLeft: level > 1 ? (level === 3 ? '96px' : '48px') : '0',
          marginTop: level > 1 ? '9px' : '0'
        }}
      >
        {/* Horizontal connector line for children */}
        {level > 1 && (
          <div style={{
            position: 'absolute',
            left: '-24px',
            top: '50%',
            width: '20px',
            height: '2px',
            background: theme.primary
          }} />
        )}

        <OwnedGroupCard
          group={group}
          currentUser={currentUser}
          onVisit={handleVisitSuggestedGroup}
          onEdit={(group) => {
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
              detail: { view: 'new-group', mode: 'edit', group: group }
            }));
          }}
          onAdmin={(group) => {
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
              detail: { view: 'admin-group', group: group }
            }));
          }}
        />

        {/* Vertical connector line for children */}
        {hasChildren && (
          <div style={{
            position: 'absolute',
            left: '24px',
            top: '120px',
            bottom: '0',
            width: '2px',
            background: theme.primary
          }} />
        )}

        {/* Render children */}
        {hasChildren && (
          <div style={{ position: 'relative' }}>
            {children.map((child) =>
              renderGroupHierarchy(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      width: '100%',
      // GroupsGrid is always embedded in MainFeed's row, which already computes the correct
      // available height (marginTop/marginBottom sourced from useMediaQuery + minHeight:0 on
      // the row so it shrinks to fit rather than growing past the shell). height:'100%'
      // inherits that instead of recomputing it — this is the exact pairing the MainFeed
      // comment on the group-discovery marginTop refers to ("GroupsGrid sizes to
      // calc(100vh - 50px), so the offset must match... a phantom gap"). Deleting this side's
      // arithmetic removes the possibility of the two drifting again, rather than keeping them
      // in sync by convention.
      height: '100%',
      background: theme.bg,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'  // Added for search bar positioning
    }}>

      {joinFeedback && (
        <div style={{
          position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 20, padding: '8px 16px', borderRadius: '8px',
          background: '#EF4444', color: '#fff', fontSize: '13px', textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxWidth: '90%'
        }}>
          {joinFeedback}
        </div>
      )}

      {/* Fixed Radio Button Toggle at Top - with 20px indent and rounded corners */}
      <div style={{
        flexShrink: 0,
        margin: '0 20px',
        borderRadius: '0 0 10px 10px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 12px',
          minHeight: '44px',
          background: 'white',
          borderRadius: '0 0 10px 10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          {/* Radio buttons centered */}
          <div style={{ display: 'flex', gap: isMobile ? '12px' : '24px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: viewMode === 'joined' ? '600' : '400',
              color: viewMode === 'joined' ? theme.primary : '#64748b'
            }}>
              <input
                type="radio"
                name="viewMode"
                checked={viewMode === 'joined'}
                onChange={() => setViewMode('joined')}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: theme.primary
                }}
              />
              {t('groups_ive_joined')}
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: viewMode === 'owned' ? '600' : '400',
              color: viewMode === 'owned' ? theme.primary : '#64748b'
            }}>
              <input
                type="radio"
                name="viewMode"
                checked={viewMode === 'owned'}
                onChange={() => setViewMode('owned')}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: theme.primary
                }}
              />
              {t('groups_i_own')}
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '15px',
              fontWeight: viewMode === 'discover' ? '600' : '400',
              color: viewMode === 'discover' ? theme.primary : '#64748b'
            }}>
              <input
                type="radio"
                name="viewMode"
                checked={viewMode === 'discover'}
                onChange={() => setViewMode('discover')}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: theme.primary
                }}
              />
              {t('discover_groups')}
            </label>

            {/* Create Group button — persistent across discovery view modes (joined/owned/discover)
                and on mobile. Previously gated to owned+desktop, which left Discovery and the whole
                mobile surface with no Create Group entry point (the compact discovery header has none). */}
            <button
              onClick={handleCreateGroup}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 16px',
                marginLeft: isMobile ? '0' : '24px',
                background: theme.accent,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                flexShrink: 0
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Plus size={16} />
              {t('create_group_button')}
            </button>

          </div>
        </div>
      </div>

      {/* Search Results View */}
      {isSearching ? (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 12px 12px 12px'
        }}>
          {/* Search header with clear button */}
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: theme.primary,
              margin: 0
            }}>
              {t('search_results')} ({searchResults.length})
            </h3>
            <button
              onClick={() => handleGroupSearch('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.primary,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {t('clear_search')}
            </button>
          </div>

          {/* Search results grid */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {searchResults.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px' }}>
                {t('no_search_results', { term: searchTerm })}
              </div>
            ) : (
              searchResults.map(group => (
                <JoinedGroupCard
                  key={group.id}
                  group={group}
                  onClick={handleVisitSuggestedGroup}
                  onJoin={handleJoinGroup}
                  isSuggested={true}
                />
              ))
            )}
          </div>
        </div>
      ) : null}

      {/* Groups I've Joined View */}
      {!isSearching && viewMode === 'joined' && (
        <>
          {/* Scrollable Joined Groups Area */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 12px 12px 12px'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              {showLoading ? (
                <div style={{ color: theme.primary, padding: '12px' }}>{t('loading_groups')}</div>
              ) : topLevelJoinedGroups.length === 0 ? (
                <div style={{ color: '#64748b', padding: '12px' }}>{t('no_groups_joined_yet')}</div>
              ) : (
                topLevelJoinedGroups.map(group => (
                  <JoinedGroupCard
                    key={group.id}
                    group={group}
                    onClick={handleGroupClick}
                  />
                ))
              )}
            </div>
          </div>

          {/* Fixed Suggested Groups at Bottom - raised above MobileBottomBar on mobile */}
          <div style={{
            flexShrink: 0,
            background: theme.bg,
            borderTop: '1px solid #e5e7eb',
            padding: isMobile ? '8px 12px 72px 12px' : '8px 12px 12px 12px',
            position: 'relative'
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: theme.primary,
              marginBottom: '8px',
              marginTop: 0,
              textAlign: 'center'
            }}>
              {t('suggested_groups_header')}
            </h3>
            <div style={{ position: 'relative' }}>
              {/* Left scroll arrow */}
              {showLeftArrow && (
                <button
                  onClick={() => scrollSuggested('left')}
                  style={{
                    position: 'absolute',
                    left: '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: `2px solid ${theme.primary}`,
                    color: theme.primary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = theme.primary;
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.color = theme.primary;
                  }}
                >
                  <ChevronLeft size={20} />
                </button>
              )}

              {/* Right scroll arrow */}
              {showRightArrow && (
                <button
                  onClick={() => scrollSuggested('right')}
                  style={{
                    position: 'absolute',
                    right: '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: `2px solid ${theme.primary}`,
                    color: theme.primary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = theme.primary;
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.color = theme.primary;
                  }}
                >
                  <ChevronRight size={20} />
                </button>
              )}

              {/* Scrollable container */}
              <div
                ref={suggestedScrollRef}
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: '12px',
                  overflowX: 'auto',
                  paddingBottom: '4px',
                  scrollbarWidth: 'thin',
                  scrollBehavior: 'smooth'
                }}
              >
                {showLoading ? (
                  <div style={{ color: theme.primary, padding: '12px' }}>{t('loading')}</div>
                ) : suggestedGroups.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '12px' }}>{t('no_suggested_groups')}</div>
                ) : (
                  suggestedGroups.map(group => (
                    <JoinedGroupCard
                      key={group.id}
                      group={group}
                      onClick={handleVisitSuggestedGroup}
                      onJoin={handleJoinGroup}
                      isSuggested={true}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Groups I Own View */}
      {!isSearching && viewMode === 'owned' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 12px 12px 12px'
        }}>
          {/* Owned Groups - Multi-column flex-wrap layout */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {showLoading ? (
              <div style={{ color: theme.primary, padding: '12px' }}>{t('loading')}</div>
            ) : ownedGroups.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px' }}>
                {t('no_owned_groups_yet')}
              </div>
            ) : (
              topLevelOwnedGroups.map((group) =>
                renderGroupHierarchy(group, 1)
              )
            )}
          </div>
        </div>
      )}

      {/* Discover Groups View - All public and publicly-visible private groups */}
      {!isSearching && viewMode === 'discover' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 12px 12px 12px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {showLoading ? (
              <div style={{ color: theme.primary, padding: '12px' }}>{t('loading')}</div>
            ) : discoverGroups.length === 0 ? (
              <div style={{ color: '#64748b', padding: '12px' }}>{t('no_groups_available')}</div>
            ) : (
              discoverGroups.map(group => (
                <JoinedGroupCard
                  key={group.id}
                  group={group}
                  onClick={handleVisitSuggestedGroup}
                  onJoin={handleJoinGroup}
                  isSuggested={true}
                />
              ))
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default GroupsGrid;
